import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { APMClient, VIEW_VERSIONS_V12 } from '@cognite/apm-client';
import { Heading } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Loader } from '@cognite/cogs-core-v11/components/loader/loader.tsx';
import { useAuthContext } from '@cognite/e2e-auth';

import {
  USER_INSTANCE_SPACE,
  USER_MODEL_NAME,
  USER_MODEL_SPACE,
  USER_MODEL_VERSION,
  USER_PREFERENCES_VIEW_NAME,
  USER_VIEW_NAME,
} from '../../constants/user-data-model';
import { ThreeDService } from '../../features/3d/3d-service';
import { ActivityService } from '../../features/activities/activity-service';
import { defaultAppConfig } from '../../features/app-config/default-config';
import { selectedRootLocationAtom } from '../../features/app-config/state/state';
import type {
  AppConfig,
  RootLocationConfiguration,
} from '../../features/app-config/types';
import {
  getIsNewConfigVersion,
  SELECTED_ROOT_LOCATION_KEY,
} from '../../features/app-config/utils/utils';
import { AssetService } from '../../features/asset/asset-service';
import { ChecklistSchedulerService } from '../../features/checklist/checklist-scheduler/checklist-scheduler-service';
import { ChecklistService } from '../../features/checklist/checklist-service';
import { ActionsService } from '../../features/conditions/actions/actions-service';
import { ConditionalActionsService } from '../../features/conditions/conditional-actions/conditional-actions-service';
import { ConditionsService } from '../../features/conditions/conditions/conditions.service';
import { createIdmMediaService } from '../../features/idm-media/services/idm-media-service';
import { createIdmObservationsService } from '../../features/idm-observations/services/idm-observations-service';
import { createIdmSearchService } from '../../features/idm-search/services/idm-search-service';
import { MeasurementService } from '../../features/measurements/measurement-service';
import { MediaService } from '../../features/media/media-service';
import { NotificationService } from '../../features/notifications/notification-service';
import { ObservationService } from '../../features/observation/observation-service';
import { IntervalService } from '../../features/task/interval-list/interval-service';
import { TemplateService } from '../../features/template/template-service';
import { TimeseriesService } from '../../features/timeseries/timeseries-service';
import { ViewService } from '../../features/view/services/view-service';
import { useGetViewsByReferences } from '../hooks/use-get-views-by-references';
import { useAppConfigContext } from '../is-idm-provider/app-config-provider';
import { removeEntityFromView } from '../is-idm-provider/utils/remove-entity-from-view';

import { FDMServicesContext } from './fdm-services.context';
import { setIdmMigrators } from './set-idm-migrators';
import {
  getSourceDataInstanceSpaceId,
  getSourceViewsFromApmOrIdmConfig,
} from './utils';

export interface FDMServicesProviderProps {
  mocks?: {
    appConfig: AppConfig;
  };
  localAppInstanceSpace?: string;
}

export const FDMServicesProvider: FunctionComponent<
  PropsWithChildren<FDMServicesProviderProps>
> = ({ mocks, localAppInstanceSpace, children }) => {
  const { t } = useTranslation('checklist');

  const appDataModelVersion = 'v12';

  const { client: cogniteClient } = useAuthContext();
  const { appConfig, location, isIdm } = useAppConfigContext();
  const isNewConfigVersion = getIsNewConfigVersion(appConfig);
  const selectedRootLocation = useRecoilValue(selectedRootLocationAtom);
  const selectedRootLocationFromLocalStorage = localStorage.getItem(
    SELECTED_ROOT_LOCATION_KEY
  );

  const selectedRootLocationConfigData: RootLocationConfiguration | undefined =
    appConfig?.featureConfiguration?.rootLocationConfigurations?.find(
      (configuredRootLocation) => {
        const selectedExternalId =
          selectedRootLocation?.externalId ??
          selectedRootLocationFromLocalStorage;
        const selectedAssetExternalId =
          selectedRootLocation?.assetExternalId ??
          selectedRootLocationFromLocalStorage;
        if (isNewConfigVersion) {
          return configuredRootLocation.externalId === selectedExternalId;
        }
        return (
          configuredRootLocation.assetExternalId === selectedExternalId ||
          configuredRootLocation.assetExternalId === selectedAssetExternalId
        );
      }
    );

  const selectedAppDataInstanceSpaceId =
    location?.appInstanceSpace ??
    selectedRootLocationConfigData?.appDataInstanceSpace ??
    appConfig?.appDataSpaceId;

  const selectedSourceDataInstanceSpaceId = getSourceDataInstanceSpaceId({
    location,
    selectedRootLocationConfigData,
    appConfig,
    isIdm,
  });
  const { assetsView, activitiesView, operationsView, notificationsView } =
    useMemo(
      () =>
        getSourceViewsFromApmOrIdmConfig({
          appConfig,
          isIdm,
          idmConfig: location,
        }),
      [appConfig, isIdm, location]
    );

  const baseAssetViewReference = useMemo(() => {
    return {
      externalId: !isIdm ? 'Asset' : 'CogniteAsset',
      space: !isIdm ? 'cdf_core' : 'cdf_cdm',
      version: 'v1',
    };
  }, [isIdm]);

  const baseTimeseriesViewReference = useMemo(
    () =>
      isIdm
        ? {
            externalId: 'CogniteTimeSeries',
            space: 'cdf_cdm',
            version: VIEW_VERSIONS_V12.TIMESERIES,
          }
        : undefined,
    [isIdm]
  );

  const configuredAssetViewReference = removeEntityFromView(
    location?.views?.find((view) => view.representsEntity === 'ASSET')
  ) || { ...baseAssetViewReference };

  const apmConfig = useMemo(
    () =>
      new APMClient(
        {
          cogniteClient,
          appDataModelSpace: 'cdf_apm',
          appDataModelName: 'ApmAppData',
          appDataModelVersion,
          appDataInstanceSpace:
            localAppInstanceSpace ??
            selectedAppDataInstanceSpaceId ??
            defaultAppConfig.appDataSpaceId!,
          sourceDataModelSpace:
            appConfig?.customerDataSpaceId ??
            defaultAppConfig.customerDataSpaceId!,
          sourceDataModelName:
            appConfig?.customerDataSpaceId ??
            defaultAppConfig.customerDataSpaceId!,
          sourceDataModelVersion:
            appConfig?.customerDataSpaceVersion ??
            defaultAppConfig.customerDataSpaceVersion!,
          sourceDataInstanceSpace: selectedSourceDataInstanceSpaceId,
        },
        {
          userViewName: USER_VIEW_NAME,
          userModelSpace: USER_MODEL_SPACE,
          userModelName: USER_MODEL_NAME,
          userInstanceSpace: USER_INSTANCE_SPACE,
          userModelVersion: USER_MODEL_VERSION,
          userPreferencesViewName: USER_PREFERENCES_VIEW_NAME,
          activitiesView: activitiesView,
          operationsView: operationsView,
          notificationsView: notificationsView,
          assetsView: assetsView,
          filesView: isIdm
            ? {
                externalId: 'CogniteFile',
                space: 'cdf_cdm',
                version: 'v1',
              }
            : undefined,
          timeseriesView: baseTimeseriesViewReference,
        }
      ),
    [
      cogniteClient,
      appDataModelVersion,
      localAppInstanceSpace,
      selectedAppDataInstanceSpaceId,
      appConfig?.customerDataSpaceId,
      appConfig?.customerDataSpaceVersion,
      selectedSourceDataInstanceSpaceId,
      baseTimeseriesViewReference,
      isIdm,
      activitiesView,
      operationsView,
      notificationsView,
      assetsView,
    ]
  );

  const {
    data: [baseAssetView, customAssetView] = [undefined, undefined],
    isLoading: isLoadingViews,
  } = useGetViewsByReferences(
    [baseAssetViewReference, configuredAssetViewReference],
    Boolean(appConfig)
  );

  const fdmServices = useMemo(() => {
    if ((!isLoadingViews && baseAssetView) || mocks) {
      if (isIdm) {
        setIdmMigrators(apmConfig, baseAssetView, customAssetView);
      }

      const viewService = new ViewService(cogniteClient);
      return {
        activityService: new ActivityService(cogniteClient, apmConfig),
        assetService: new AssetService(cogniteClient, apmConfig),
        notificationService: new NotificationService(apmConfig),
        mediaService: new MediaService(cogniteClient, apmConfig, isIdm),
        threeDService: new ThreeDService(cogniteClient, apmConfig),
        measurementsService: new MeasurementService(cogniteClient, apmConfig),
        conditionsService: new ConditionsService(cogniteClient, apmConfig),
        conditionalActionsService: new ConditionalActionsService(
          cogniteClient,
          apmConfig
        ),
        actionsService: new ActionsService(cogniteClient, apmConfig),
        timeseriesService: new TimeseriesService(cogniteClient, apmConfig),
        templateService: new TemplateService(cogniteClient, apmConfig),
        checklistService: new ChecklistService(cogniteClient, apmConfig),
        apmClient: apmConfig,
        intervalService: new IntervalService(cogniteClient, apmConfig),
        observationService: new ObservationService(cogniteClient, apmConfig),
        checklistSchedulerService: new ChecklistSchedulerService(cogniteClient),
        viewService,
        idmObservationService: createIdmObservationsService({
          client: cogniteClient,
          viewService,
        }),
        idmSearchService: createIdmSearchService(cogniteClient),
        idmMediaService: createIdmMediaService(cogniteClient),
      };
    }
  }, [
    isLoadingViews,
    baseAssetView,
    mocks,
    cogniteClient,
    apmConfig,
    isIdm,
    customAssetView,
  ]);

  if (fdmServices || mocks) {
    return (
      <FDMServicesContext.Provider value={fdmServices}>
        {children}
      </FDMServicesContext.Provider>
    );
  }

  return (
    <Loader
      infoText={
        <Heading level={6}>
          {t(
            'SERVICE_PROVIDER_LOADING_APP_CONFIGURATION',
            'Loading app configuration'
          )}
        </Heading>
      }
    />
  );
};
